import { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useInvitationInfo } from "../../api/hooks/useInvitationInfo";
import { useMyUser } from "../../features/user/hooks/useMyUser";
import authAPI from "../../api/authAPI";
import invitationAPI from "../../api/invitationAPI";
import Spinner from "../_shared/Spinner/Spinner";
import styles from "./InvitationAccept.module.css";
import { LogoForsknigsradet, LogoJaerskulen, LogoLesesenteret } from "../../common/Logos/Logos";
import {
  doGetFeideLoginUrl,
  doSignup,
  selectFeideUrl,
  selectLoginError,
  selectIsLoggingIn,
} from "../../features/auth/AuthSlice";

const InvitationAccept = () => {
  const { t } = useTranslation("common");
  const { token } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: currentUser } = useMyUser();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");

  const feideUrl = useSelector(selectFeideUrl);
  const loginError = useSelector(selectLoginError);
  const isLoggingIn = useSelector(selectIsLoggingIn);

  // Add background class to body
  useEffect(() => {
    document.body.classList.add(styles.background);
    return () => document.body.classList.remove(styles.background);
  }, []);

  // Get Feide URL
  useEffect(() => {
    if (!feideUrl) dispatch(doGetFeideLoginUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feideUrl]);

  const { data: invitationData, isLoading, isError, error } = useInvitationInfo(token);
  const invitation = invitationData;

  const handleAcceptInvitation = async () => {
    if (!currentUser || !invitation) return;

    try {
      const response = await invitationAPI.acceptInvitation(token);

      // Redirect based on invitation type
      if (invitation.type === "group") {
        history.push('/settings/organizations');
      } else if (invitation.type === "subject-plan") {
        history.push(`/temaplan/visning/${response.data.id}`);
      } else if (invitation.type === "year-plan") {
        history.push(`/arsplan/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
    }
  };

  const getInvitationTypeLabel = () => {
    switch (invitation?.type) {
      case "group":
        return t("invitation.types.group");
      case "subject-plan":
        return t("invitation.types.subjectPlan");
      case "year-plan":
        return t("invitation.types.yearPlan");
      default:
        return "";
    }
  };

  const getRoleLabel = () => {
    if (invitation?.type === "group" && invitation?.roleName) {
      return t(`auth.settings.organizations.roles.${invitation.roleName}`);
    }
    if (invitation?.accessLevel) {
      const accessMap = {
        A: t("sharePlan.asAdmin"),
        W: t("toEdit"),
        R: t("toRead"),
      };
      return accessMap[invitation.accessLevel] || "";
    }
    return "";
  };

  const handleShowPasswordForm = () => {
    setShowPasswordForm(true);
  };

  const handlePasswordSignup = async (e) => {
    e.preventDefault();
    const result = await dispatch(doSignup({
      email: invitation.email,
      fullName,
      password
    }));

    if (doSignup.fulfilled.match(result)) {
      // After successful signup, the user is logged in
      // The useEffect will detect this and complete the invitation
      window.location.reload();
    }
  };

  const handleFeideClick = () => {
    // Store invitation token in localStorage for Feide callback
    localStorage.setItem("pendingInvitation", token);
    window.location.href = feideUrl;
  };

  const handleLoginClick = (method) => {
    if (method === "feide") {
      handleFeideClick();
    } else {
      // Redirect to login with invitation context
      history.push(`/login?invitationToken=${token}&email=${encodeURIComponent(invitation.email)}`);
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    window.location.reload();
  };

  if (isLoading) {
    return (
      <>
        <main className={styles.main}>
          <Spinner />
        </main>
        <footer className={styles.footer}>
          <section className={styles.logos}>
            <LogoJaerskulen />
            <LogoLesesenteret />
            <LogoForsknigsradet />
          </section>
        </footer>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <main className={styles.main}>
          <h1 className={styles.title}>{t("invitation.error.title")}</h1>
          <p className={styles.subTitle}>{error?.response?.data?.message || t("invitation.error.notFound")}</p>
          <button className={styles.button} onClick={() => history.push("/")}>
            {t("invitation.error.goHome")}
          </button>
        </main>
        <footer className={styles.footer}>
          <section className={styles.logos}>
            <LogoJaerskulen />
            <LogoLesesenteret />
            <LogoForsknigsradet />
          </section>
        </footer>
      </>
    );
  }

  // If logged in but email doesn't match
  if (currentUser && currentUser.email !== invitation.email) {
    return (
      <>
        <main className={styles.main}>
          <h1 className={styles.title}>{t("invitation.wrongEmail.title")}</h1>
          <p className={styles.subTitle}>
            {t("invitation.wrongEmail.message", {
              invitedEmail: invitation.email,
              currentEmail: currentUser.email,
            })}
          </p>
          <button className={styles.button} onClick={handleLogout}>
            {t("invitation.wrongEmail.logout")}
          </button>
          <button
            className={styles.toggleMode}
            onClick={() => history.push("/")}
          >
            {t("cancel")}
          </button>
        </main>
        <footer className={styles.footer}>
          <section className={styles.logos}>
            <LogoJaerskulen />
            <LogoLesesenteret />
            <LogoForsknigsradet />
          </section>
        </footer>
      </>
    );
  }

  // Show invitation details and auth options
  // Check if logged in user matches invitation email
  const isMatchingUser = currentUser && currentUser.email === invitation.email;

  return (
    <>
      <main className={styles.main}>
        <h1 className={styles.title}>{t("invitation.title")}</h1>

        <div className={styles.invitationInfo}>
          <p className={styles.invitedTo}>
            {t("invitation.invitedTo", {
              type: getInvitationTypeLabel(),
              name: invitation.targetName,
            })}
          </p>
          {getRoleLabel() && (
            <p className={styles.role}>
              {t("invitation.withAccess")}: <strong>{getRoleLabel()}</strong>
            </p>
          )}
          <p className={styles.email}>
            {t("invitation.forEmail")}: <strong>{invitation.email}</strong>
          </p>
        </div>

        <div className={styles.authSection}>
          {isMatchingUser ? (
            // User is logged in with matching email - show accept button
            <>
              <h2 className={styles.subTitle}>
                {t("invitation.acceptInvitation.title")}
              </h2>
              <p className={styles.authDescription}>
                {t("invitation.acceptInvitation.message")}
              </p>
              <button
                className={styles.button}
                style={{ minWidth: 275 }}
                onClick={handleAcceptInvitation}
              >
                {t("invitation.acceptInvitation.button")}
              </button>
            </>
          ) : (
            <>
              <h2 className={styles.subTitle}>
                {invitation.userExists
                  ? t("invitation.existingUser.title")
                  : t("invitation.newUser.title")}
              </h2>
              <p className={styles.authDescription}>
                {invitation.userExists
                  ? t("invitation.existingUser.message")
                  : t("invitation.newUser.message")}
              </p>

              {invitation.userExists ? (
            // Existing user - show login options
            <div className={styles.buttonGroup}>
              {invitation.authMethods?.hasPassword && (
                <button
                  className={styles.button}
                  style={{ minWidth: 275 }}
                  onClick={() => handleLoginClick("password")}
                >
                  {t("invitation.existingUser.loginWithPassword")}
                </button>
              )}
              {invitation.authMethods?.hasFeide && (
                <button
                  className={styles.button}
                  style={{ minWidth: 275 }}
                  onClick={() => handleLoginClick("feide")}
                  disabled={!feideUrl}
                >
                  {t("invitation.existingUser.loginWithFeide")}{" "}
                  <img
                    src="/images/feide-logo.png"
                    alt="Feide"
                    width="92"
                    height="32"
                  />
                </button>
              )}
            </div>
          ) : showPasswordForm ? (
            // New user - show password signup form
            <form className={styles.loginForm} onSubmit={handlePasswordSignup}>
              <div className={styles.inputGroup}>
                <label htmlFor="fullName">{t("auth.signup.fullName")}</label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  disabled={isLoggingIn}
                  className={styles.input}
                  autoFocus
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="email">{t("auth.email")}</label>
                <input
                  type="email"
                  id="email"
                  value={invitation.email}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="password">{t("auth.password")}</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoggingIn}
                  className={styles.input}
                  minLength={8}
                />
              </div>

              {loginError && (
                <div className={styles.error}>
                  {loginError.message || t("auth.signup.signupFailed")}
                </div>
              )}

              <button
                type="submit"
                className={styles.button}
                disabled={isLoggingIn}
                style={{ minWidth: 275 }}
              >
                {isLoggingIn
                  ? t("auth.signup.signingUp")
                  : t("auth.signup.signupButton")}
              </button>

              <button
                type="button"
                onClick={() => setShowPasswordForm(false)}
                className={styles.toggleMode}
              >
                {t("cancel")}
              </button>
            </form>
          ) : (
            // New user - show signup options
            <div className={styles.buttonGroup}>
              <button
                className={styles.button}
                style={{ minWidth: 275 }}
                onClick={handleShowPasswordForm}
              >
                {t("invitation.newUser.signupWithPassword")}
              </button>
              <div className={styles.divider}>{t("auth.or")}</div>
              <button
                className={styles.button}
                style={{ minWidth: 275 }}
                onClick={handleFeideClick}
                disabled={!feideUrl}
              >
                {t("invitation.newUser.signupWithFeide")}{" "}
                <img
                  src="/images/feide-logo.png"
                  alt="Feide"
                  width="92"
                  height="32"
                />
              </button>
            </div>
          )}
            </>
          )}
        </div>
      </main>
      <footer className={styles.footer}>
        <section className={styles.logos}>
          <LogoJaerskulen />
          <LogoLesesenteret />
          <LogoForsknigsradet />
        </section>
      </footer>
    </>
  );
};

export default InvitationAccept;
